import { PhoneIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubmitButton } from "#/components/shared/buttons/submit-button";
import Input from "#/components/shared/input";
import ThemedSelect from "#/components/shared/themed-select";
import Typography from "#/components/shared/typography";
import { useGetVoiceAgents } from "#/agents/hooks/useGetVoiceAgents";
import { OutboundCallPayload } from "#/api/call/call.api";
import { useCreateOutboundCall } from "#/calls/hooks/useCreateOutboundCall";
import { useMedicalRecordRequestDetailContext } from "../context/MedicalRecordRequestDetailContext";

export function StartCallButton() {
  const { resolvedRequest, isEditable } =
    useMedicalRecordRequestDetailContext();
  const navigate = useNavigate();
  const [selectedAgentKey, setSelectedAgentKey] = useState<string>("");
  const [agentType, setAgentType] = useState<OutboundCallPayload["agent_type"]>(
    "openai_audio"
  );
  const [outboundNumber, setOutboundNumber] = useState<string>("");
  const { data: agents = [], isLoading, isError } = useGetVoiceAgents(true);
  const { mutateAsync: placeOutboundCall, isPending: isOutboundPending } =
    useCreateOutboundCall();

  const seedContext = useMemo(() => {
    if (!resolvedRequest) return null;

    const selectedAgent = agents.find((agent) => agent.key === selectedAgentKey);
    const recipientEntity =
      resolvedRequest.recipientDetails?.institution ??
      resolvedRequest.recipientDetails?.provider ??
      null;
    const contactPhone =
      recipientEntity?.contact_details?.find((detail) => detail.is_primary)
        ?.phone ??
      recipientEntity?.contact_details?.find((detail) => detail.phone)?.phone ??
      null;
    const recipientPhone =
      outboundNumber ||
      recipientEntity?.phone ||
      recipientEntity?.phoneNumber ||
      contactPhone;

    return {
      medical_record_request: resolvedRequest,
      patient: {
        id: resolvedRequest.patientId,
        name: resolvedRequest.patientName ?? resolvedRequest.patient_name ?? null,
      },
      recipient: resolvedRequest.recipientDetails ?? null,
      recipient_phone: recipientPhone,
      ...(selectedAgent ? { agent: selectedAgent } : {}),
    };
  }, [agents, outboundNumber, resolvedRequest, selectedAgentKey]);

  const defaultOutboundPhone = useMemo(() => {
    const recipientEntity =
      resolvedRequest?.recipientDetails?.institution ??
      resolvedRequest?.recipientDetails?.provider ??
      null;
    const contactPhone =
      recipientEntity?.contact_details?.find((detail) => detail.is_primary)
        ?.phone ??
      recipientEntity?.contact_details?.find((detail) => detail.phone)?.phone ??
      null;
    return (
      recipientEntity?.phone ??
      recipientEntity?.phoneNumber ??
      contactPhone ??
      ""
    );
  }, [resolvedRequest]);

  useEffect(() => {
    if (!outboundNumber && defaultOutboundPhone) {
      setOutboundNumber(defaultOutboundPhone);
    }
  }, [defaultOutboundPhone, outboundNumber]);

  if (!isEditable || !seedContext) {
    return null;
  }

  const handleStartCall = () => {
    const encoded = encodeURIComponent(JSON.stringify(seedContext));
    const agentParam = selectedAgentKey
      ? `&agent=${encodeURIComponent(selectedAgentKey)}`
      : "";
    const agentTypeParam = agentType
      ? `&agent_type=${encodeURIComponent(agentType)}`
      : "";
    const requestIdParam = resolvedRequest?.id
      ? `&medical_record_request_id=${encodeURIComponent(resolvedRequest.id)}`
      : "";
    navigate(
      `/admin/assistant-voice?seed_context=${encoded}${agentParam}${agentTypeParam}${requestIdParam}`
    );
  };

  const handleOutboundCall = async () => {
    const recipientPhone =
      outboundNumber ||
      seedContext?.recipient_phone ||
      resolvedRequest?.recipientDetails?.institution?.phone ||
      resolvedRequest?.recipientDetails?.provider?.phone ||
      null;

    if (!recipientPhone) {
      return;
    }

    if (!resolvedRequest?.id) {
      return;
    }

    const payload: OutboundCallPayload = {
      to: recipientPhone,
      medical_record_request_id: resolvedRequest.id,
      ...(selectedAgentKey ? { agent: selectedAgentKey } : {}),
      ...(agentType ? { agent_type: agentType } : {}),
    };

    await placeOutboundCall(payload);
  };

  const outboundPhone =
    outboundNumber ||
    seedContext?.recipient_phone ||
    resolvedRequest?.recipientDetails?.institution?.phone ||
    resolvedRequest?.recipientDetails?.provider?.phone ||
    null;

  return (
    <div className="flex flex-col items-end gap-3">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-cream-200 bg-white px-4 py-3 shadow-sm">
        <div className="min-w-[220px]">
          <ThemedSelect
            options={agents.map((agent) => ({
              value: agent.key,
              label: agent.name,
            }))}
            placeholder={isLoading ? "Loading agents..." : "Select an agent"}
            initialValue={selectedAgentKey}
            onChange={(event) => setSelectedAgentKey(event.target.value)}
          />
        </div>
        <div className="min-w-[200px]">
          <ThemedSelect
            options={[
              { value: "openai_audio", label: "OpenAI audio" },
              { value: "deepgram_agent", label: "Deepgram agent" },
            ]}
            placeholder="Select agent type"
            initialValue={agentType}
            onChange={(event) =>
              setAgentType(
                event.target.value as OutboundCallPayload["agent_type"]
              )
            }
          />
        </div>
        <div className="min-w-[220px]">
          <Input
            placeholder="Outbound phone"
            value={outboundNumber}
            onChange={(event) => setOutboundNumber(event.target.value)}
          />
        </div>
        <SubmitButton
          hideArrow
          color="terracotta"
          onClick={handleStartCall}
          icon={<PhoneIcon className="h-4 w-4" />}
          isDisabled={isLoading || (Boolean(agents.length) && !selectedAgentKey)}
          isLoading={isLoading}
        >
          Start realtime call
        </SubmitButton>
          <SubmitButton
            hideArrow
            color="beige"
            onClick={handleOutboundCall}
            isDisabled={!outboundPhone || isOutboundPending || !resolvedRequest?.id}
            isLoading={isOutboundPending}
          >
          Call {outboundPhone ?? "recipient"}
        </SubmitButton>
        {isError && (
          <Typography variant="body2" className="text-terracotta">
            Unable to load agents. Try again.
          </Typography>
        )}
      </div>
    </div>
  );
}
